'use client'
import { useEffect } from 'react'

/**
 * Publishes the height of whatever site chrome stays pinned to the top of the
 * viewport, as `--sticky-chrome-height` on `<html>`.
 *
 * WHY this is measured rather than declared: there is no single header height
 * to write down. The row height is `h-16` or `h-20` depending on the header
 * variant, overridden per dealer by `logo_size` (and `xl` swaps the fixed row
 * for `min-h-* py-2`, so it GROWS with the logo rather than clipping it).
 * `sticky_behavior: 'static'` means nothing is pinned at all, and
 * `BrandShowcaseHeader`'s overlay mode renders `relative … h-0`, which also
 * displaces nothing. On top of that, a dealer with imported chrome renders
 * arbitrary cloned HTML whose height this repo cannot know even in principle.
 * Any constant would therefore be correct for one dealer and wrong for the
 * next — the per-tenant failure shape that keeps recurring here.
 *
 * The measurement is deliberately narrow: sum the heights of top-anchored
 * `position: sticky|fixed` `<header>` elements outside `<main>`. A header that
 * scrolls away (static, or the overlay variant) contributes nothing, which is
 * correct — by the time anything below it is stuck, that chrome is gone. The
 * announcement bar is excluded for the same reason: it is not sticky, so it
 * has scrolled off before any offset matters. Content `<header>` elements are
 * skipped via the `<main>` check rather than trusted to never be sticky.
 *
 * Consumers must supply a fallback (`var(--sticky-chrome-height, 4rem)`): this
 * runs after hydration, and the var is simply absent during SSR and for anyone
 * with JS off. 4rem matches the default `h-16` row, so the unmeasured case is
 * approximately right rather than zero.
 */
export function StickyChromeOffset() {
  useEffect(() => {
    const pinnedHeaders = () =>
      Array.from(document.querySelectorAll('header')).filter((el) => !el.closest('main'))

    const measure = () => {
      let total = 0
      for (const el of pinnedHeaders()) {
        const cs = getComputedStyle(el)
        if (cs.position !== 'sticky' && cs.position !== 'fixed') continue
        // Anchored to the TOP. A sticky element pinned to the bottom (the PDP
        // buy bar) never displaces content below the header.
        const top = Number.parseFloat(cs.top)
        if (!Number.isFinite(top) || top > 1) continue
        total += el.getBoundingClientRect().height
      }
      document.documentElement.style.setProperty(
        '--sticky-chrome-height',
        `${Math.round(total)}px`,
      )
    }

    measure()

    // Height changes without a window resize: a logo image finishing decode, a
    // nav wrapping to a second row, a font swap. Observe the headers directly.
    const ro = new ResizeObserver(measure)
    for (const el of pinnedHeaders()) ro.observe(el)
    // A resize can change which breakpoint the header is at (`xl` is
    // `h-14 md:h-20`), and can swap a header in or out of the DOM entirely.
    window.addEventListener('resize', measure)

    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [])

  return null
}
